import { Space, Tag, Tooltip, Typography } from 'antd'
import type { TagProps } from 'antd'
import { useTranslation } from 'react-i18next'
import { utcTooltip } from '@/shared/lib/format'
import { shortId } from '@/shared/lib/shortId'
import type { EmployeeSyncState, SyncState } from './attendanceApi'

/**
 * RANG — HARAKATGA qarab, og'irlikka qarab EMAS (panel bo'ylab bir xil
 * qoida: `DeviceStatusTag`, `NasHealthTag`).
 *
 *   · `SYNCED`  YASHIL  — xodim qurilmada BOR. Hech narsa qilish kerak emas.
 *
 *   · `PENDING` SARIQ   — navbatda. ⚠️ QIZIL EMAS: bu xato emas, hali
 *                         bo'lmagan ish. Fon jarayoni uni o'zi bajaradi va
 *                         aksariyat hollarda bir necha soniyada tugaydi.
 *                         Qizil qilish har yangi xodimda yolg'on
 *                         ogohlantirish berardi — va o'sha yolg'on
 *                         ogohlantirishlar orasida HAQIQIYSI ko'rinmay
 *                         qolardi.
 *
 *   · `FAILED`  QIZIL   — qurilma RAD ETDI. O'zi tuzalmaydi: rasm sifati
 *                         past, xotira to'lgan yoki parol mos emas —
 *                         uchalasi ham odam aralashuvini talab qiladi.
 *                         Qizilning ta'rifi aynan shu.
 */
const STATE_COLOR: Record<SyncState, TagProps['color']> = {
  SYNCED: 'green',
  PENDING: 'gold',
  FAILED: 'red',
}

/**
 * Bitta qurilmadagi holat — chip ko'rinishida.
 *
 * <h2>⚠️ XATO MATNI XOM HOLDA CHIQADI</h2>
 * `lastSubStatus` — qurilmaning O'Z kodi (`lowFaceQuality`,
 * `outOfMaxRecordNum`). U ATAYLAB tarjima qilinmaydi va umumiy matn bilan
 * almashtirilmaydi. Sabab bitta jumlada: <b>`lowFaceQuality` operatorga
 * nima qilish kerakligini aytadi, "sinxronlash muvaffaqiyatsiz" esa hech
 * narsa demaydi.</b> Birinchisini o'qigan odam boshqa rasm yuklaydi;
 * ikkinchisini o'qigan odam qo'llab-quvvatlashga yozadi.
 *
 * <h2>⚠️ IKKI QADAM ALOHIDA KO'RSATILADI</h2>
 * Qurilmaga ikki chaqiruv ketadi — foydalanuvchi yozuvi va yuz rasmi —
 * va birinchisi o'tib, ikkinchisi yiqilishi ODATIY hol. Bu holatda xodim
 * qurilmada BOR, lekin yuzisiz: u eshikdan o'ta OLMAYDI, garchi ro'yxatda
 * ko'rinsa ham. Faqat "xato" deb yozish bu farqni yo'qotardi va operator
 * xodimni butunlay qayta yaratishga urinardi.
 */
function SyncChip({ row }: { row: EmployeeSyncState }) {
  const { t } = useTranslation()

  const lines: string[] = [t(`attendance.employee.syncState.${row.state}`)]

  if (row.desiredAction === 'DELETE') lines.push(t('attendance.employee.syncPendingDelete'))

  const mark = (done: boolean) =>
    done ? t('attendance.employee.syncDone') : t('attendance.employee.syncNotDone')

  lines.push(`${t('attendance.employee.syncUser')}: ${mark(row.userSyncedAt !== null)}`)
  lines.push(`${t('attendance.employee.syncFace')}: ${mark(row.faceSyncedAt !== null)}`)

  // ⚠️ Qurilmaning XOM kodi — komponent sarlavhasidagi izoh.
  if (row.lastSubStatus) lines.push(`${t('attendance.employee.syncSubStatus')}: ${row.lastSubStatus}`)
  // ⚠️ BUG-033 bilan BIR XIL SINF — xom Java matni yorliqsiz turardi.
  //    `device_employee_sync.last_error` ga `HikOperations` /
  //    `IsupGatewayClient` ning `"... yetib bo'lmadi: " + e` qatori
  //    tushadi, ya'ni tooltipda `java.net.SocketTimeoutException`
  //    ko'rinardi. Yuqoridagi qatorlar (holat, ikki qadam,
  //    `subStatus`) TARJIMA qilingan va o'z ma'nosini bildiradi; bu
  //    esa qo'llab-quvvatlash uchun — shuning uchun u BELGILANADI,
  //    o'chirilmaydi.
  if (row.lastError) lines.push(`${t('attendance.technicalDetail')}: ${row.lastError}`)

  // Urinishlar soni faqat MA'NOLI bo'lganda: bitta urinish hali xulosa
  // emas, o'ntasi esa qurilma bilan jiddiy muammo borligini aytadi.
  if (row.attempts !== null && row.attempts > 1) {
    lines.push(t('attendance.employee.syncAttempts', { count: row.attempts }))
  }
  if (row.state !== 'SYNCED' && row.nextAttemptAt) {
    lines.push(`${t('attendance.employee.syncNextAttempt')}: ${utcTooltip(row.nextAttemptAt)}`)
  }

  return (
    <Tooltip
      title={lines.map((line, index) => (
        <div key={index}>{line}</div>
      ))}
    >
      <Tag color={STATE_COLOR[row.state]} style={{ marginInlineEnd: 0 }}>
        {/* Zaxira shakl YAGONA joyda — `shared/lib/shortId.ts` (B12).
            AYNAN shu qurilma `People › Events` da ham chiziladi va u
            yerda `shortId` ishlatiladi: ikkalasi bir xil ko'rinsin. */}
        {row.deviceName ?? shortId(row.deviceId)}
      </Tag>
    </Tooltip>
  )
}

interface EmployeeSyncTagsProps {
  /** `null` = server bu ma'lumotni bermadi. Bo'sh massiv = hech qayerda yo'q. */
  sync: readonly EmployeeSyncState[] | null
}

/**
 * Xodim qaysi qurilmalarda bor — chiplar bilan.
 *
 * ⚠️ UCH HOLAT, IKKITA EMAS. `null` va bo'sh massiv boshqa-boshqa
 * javoblar va ularni birlashtirish operatorni noto'g'ri harakatga
 * yo'naltirardi:
 *   `null`      — biz BILMAYMIZ (server aytmadi). Hech narsa qilish kerak
 *                 emas, bu panel yoki kontrakt masalasi
 *   `[]`        — xodim HECH BIR qurilmada yo'q, ya'ni u eshikdan o'ta
 *                 olmaydi. Aniq fakt va aniq harakat: "Qurilmaga yuborish"
 *   to'ldirilgan — har qurilma bo'yicha alohida holat
 */
export function EmployeeSyncTags({ sync }: EmployeeSyncTagsProps) {
  const { t } = useTranslation()

  if (sync === null) {
    return (
      <Tooltip title={t('attendance.employee.syncUnknownHint')}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('attendance.employee.syncUnknown')}
        </Typography.Text>
      </Tooltip>
    )
  }

  if (sync.length === 0) {
    return (
      <Tooltip title={t('attendance.employee.syncNoneHint')}>
        <Tag color="red">{t('attendance.employee.syncNone')}</Tag>
      </Tooltip>
    )
  }

  return (
    <Space size={4} wrap>
      {sync.map((row) => (
        <SyncChip key={row.deviceId} row={row} />
      ))}
    </Space>
  )
}
